/** 값 반영에 성공한 셀의 uniqueId → 반영 직후 저장값 */
export type AppliedCellValueSnapshot = Record<string, string>;

export interface PasteFeedback {
  failedCellUniqueIds: string[];
  failedCellRawValues: Record<string, string>;
  /**
   * 값 반영에 성공한 셀의 반영 직후 저장값. 이 중 하나가 달라질 때까지 값 반영
   * 버튼을 잠근다. 버튼이 있는 행은 가상화로 unmount되므로 잠금 상태를 버튼
   * 로컬 state가 아닌 실패 셀 표시와 같은 행 단위 히스토리에 둔다.
   */
  appliedCellSnapshot: AppliedCellValueSnapshot | null;
  /** 값 반영으로 값이 들어간 셀의 identity 키. 반영 후에만 띄울 셀 알림에 쓴다. */
  appliedIdentityKeys?: string[];
  /**
   * 반영 직후 단위 배율 알림 패널을 이미 열었는가. 행은 가상화로 다시 마운트되므로
   * 행 로컬 상태가 아닌 이 히스토리에 둬야 "반영 한 번에 한 번만"이 지켜진다.
   */
  unitNoticeOpened?: boolean;
  undoDepth: number;
}

export type PasteFeedbackHistory = Map<string, PasteFeedback[]>;

export const appendPasteFeedback = (
  history: PasteFeedbackHistory,
  rowKey: string,
  feedback: PasteFeedback,
): PasteFeedbackHistory => {
  const next = new Map(history);
  next.set(rowKey, [...(history.get(rowKey) ?? []), feedback]);
  return next;
};

export const trimPasteFeedbackHistory = (
  history: PasteFeedbackHistory,
  undoDepth: number,
): PasteFeedbackHistory => {
  let changed = false;
  const next = new Map<string, PasteFeedback[]>();

  for (const [rowKey, entries] of history) {
    const remaining = entries.filter((entry) => entry.undoDepth <= undoDepth);
    if (remaining.length !== entries.length) changed = true;
    if (remaining.length > 0) next.set(rowKey, remaining);
  }

  return changed ? next : history;
};

/** 반영 잠금만 풀고 실패 셀 표시는 남긴다. */
export const releaseApplyLock = (
  history: PasteFeedbackHistory,
  rowKey: string,
): PasteFeedbackHistory => {
  const entries = history.get(rowKey);
  const active = entries?.at(-1);
  if (!entries || !active?.appliedCellSnapshot) return history;

  const next = new Map(history);
  next.set(rowKey, [
    ...entries.slice(0, -1),
    { ...active, appliedCellSnapshot: null },
  ]);
  return next;
};

/** 반영 직후 단위 배율 알림 패널을 열었다고 기록한다. */
export const markUnitNoticeOpened = (
  history: PasteFeedbackHistory,
  rowKey: string,
): PasteFeedbackHistory => {
  const entries = history.get(rowKey);
  const active = entries?.at(-1);
  if (!entries || !active || active.unitNoticeOpened) return history;

  const next = new Map(history);
  next.set(rowKey, [
    ...entries.slice(0, -1),
    { ...active, unitNoticeOpened: true },
  ]);
  return next;
};

export const getActivePasteFeedback = (
  history: PasteFeedbackHistory,
  rowKey: string,
): PasteFeedback | undefined => history.get(rowKey)?.at(-1);

/** 실패 당시 값에서 직접 달라진 셀 ID. DOM에서 빠진 셀은 미해결로 유지한다. */
export const getResolvedFailedCellUniqueIds = (
  feedback: PasteFeedback,
  targetRoot: HTMLElement,
): Set<string> => {
  const resolved = new Set<string>();
  for (const uniqueId of feedback.failedCellUniqueIds) {
    if (!(uniqueId in feedback.failedCellRawValues)) continue;
    const cell = targetRoot.ownerDocument.getElementById(uniqueId);
    if (!cell || !targetRoot.contains(cell)) continue;
    const currentRawValue = cell.getAttribute("data-raw-value") ?? "";
    if (currentRawValue !== feedback.failedCellRawValues[uniqueId]) {
      resolved.add(uniqueId);
    }
  }
  return resolved;
};
